/**
 * useCSVExport Composable
 *
 * Generic CSV export utility for saved views list views.
 * Fetches ALL matching results (across all pages) and exports as CSV.
 *
 * Usage:
 *   const { isExporting, exportProgress, exportAll } = useCSVExport()
 *   await exportAll({ columns, filename, entityType, filters, sort })
 */

import { ref } from 'vue'
import { savedViewService } from '../api/service'
import type { SavedViewEntityType, FilterGroup, SortConfig } from '../api/types'

// ─── Types ──────────────────────────────────────────────────────────

/** Column definition for CSV export */
export interface ExportColumn {
    /** Dot-notation field path (e.g. 'property.name', 'firstName') */
    key: string
    /** Human-readable column header */
    label: string
    /**
     * Optional value formatter. Receives the resolved value and the full row.
     * Return a string for the CSV cell. If omitted, the value is coerced via String().
     */
    format?: (value: unknown, row: Record<string, unknown>) => string
}

/** Options for a CSV export operation */
export interface ExportOptions {
    /** Column definitions controlling which fields appear and how */
    columns: ExportColumn[]
    /** Filename prefix (e.g. 'contacts-export'). Date suffix is appended automatically. */
    filename: string
    /** Entity type for the saved views preview API */
    entityType: SavedViewEntityType
    /** Current filter state (if any) */
    filters?: FilterGroup
    /** Current sort state (if any) */
    sort?: SortConfig
}

/** Progress state during export */
export interface ExportProgress {
    current: number
    total: number
}

// ─── Constants ──────────────────────────────────────────────────────

/** Batch size for paginated fetching during export */
const EXPORT_BATCH_SIZE = 100

// ─── Helpers ────────────────────────────────────────────────────────

/**
 * Resolve a dot-notation key path on an object.
 * e.g. resolveKey({ property: { name: 'Acme' } }, 'property.name') => 'Acme'
 */
function resolveKey(row: Record<string, unknown>, key: string): unknown {
    const parts = key.split('.')
    let current: unknown = row
    for (const part of parts) {
        if (current == null || typeof current !== 'object') return undefined
        current = (current as Record<string, unknown>)[part]
    }
    return current
}

/**
 * Escape a cell value for CSV.
 * Handles commas, double quotes, newlines, and CSV injection prevention.
 */
function escapeCSVCell(value: unknown): string {
    if (value == null) return ''
    const str = String(value)
    if (str.length === 0) return ''

    // CSV injection prevention: prefix cells starting with formula chars
    // with a single quote so spreadsheets don't interpret them as formulas
    const firstChar = str.charAt(0)
    const formulaChars = ['=', '+', '-', '@', '\t', '\r']
    const needsFormulaGuard = formulaChars.includes(firstChar)

    const needsQuoting =
        str.includes(',') ||
        str.includes('"') ||
        str.includes('\n') ||
        str.includes('\r') ||
        needsFormulaGuard

    if (!needsQuoting) return str

    // Escape double quotes by doubling them, then wrap in quotes
    const escaped = str.replace(/"/g, '""')
    return needsFormulaGuard ? `"'${escaped}"` : `"${escaped}"`
}

/**
 * Extract a cell value from a row using the column definition.
 * Applies the column's format function if provided, otherwise coerces to string.
 */
function extractCellValue(
    row: Record<string, unknown>,
    column: ExportColumn,
): string {
    const rawValue = resolveKey(row, column.key)

    if (column.format) {
        const formatted = column.format(rawValue, row)
        return escapeCSVCell(formatted)
    }

    // Default handling for arrays: join with semicolons
    if (Array.isArray(rawValue)) {
        return escapeCSVCell(rawValue.join('; '))
    }

    return escapeCSVCell(rawValue)
}

// ─── Composable ─────────────────────────────────────────────────────

export function useCSVExport() {
    const isExporting = ref(false)
    const exportProgress = ref<ExportProgress | null>(null)

    /**
     * Export all matching results as a CSV file.
     *
     * Fetches all pages from the saved views preview API,
     * builds a CSV string, and triggers a browser download.
     */
    async function exportAll(options: ExportOptions): Promise<void> {
        const { columns, filename, entityType, filters, sort } = options

        if (isExporting.value) return // Prevent concurrent exports

        isExporting.value = true
        exportProgress.value = { current: 0, total: 0 }

        try {
            // 1. Fetch first page to get total count
            const firstPage = await savedViewService.preview({
                entityType,
                filters: filters ?? { operator: 'AND', conditions: [] },
                sort,
                page: 1,
                limit: EXPORT_BATCH_SIZE,
            })

            const total = firstPage.meta.total
            exportProgress.value = { current: firstPage.data.length, total }

            // Collect all rows
            const allRows: Record<string, unknown>[] = [
                ...firstPage.data,
            ]

            // 2. Fetch remaining pages
            const totalPages = Math.ceil(total / EXPORT_BATCH_SIZE)

            for (let page = 2; page <= totalPages; page++) {
                const response = await savedViewService.preview({
                    entityType,
                    filters: filters ?? { operator: 'AND', conditions: [] },
                    sort,
                    page,
                    limit: EXPORT_BATCH_SIZE,
                })

                allRows.push(...response.data)
                exportProgress.value = { current: allRows.length, total }
            }

            // 3. Build CSV content
            const headerRow = columns.map((col) => escapeCSVCell(col.label)).join(',')
            const dataRows = allRows.map((row) =>
                columns.map((col) => extractCellValue(row, col)).join(','),
            )

            // UTF-8 BOM for Excel compatibility
            const BOM = '\uFEFF'
            const csvContent = BOM + [headerRow, ...dataRows].join('\n')

            // 4. Trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`
            link.click()
            URL.revokeObjectURL(url)
        } finally {
            isExporting.value = false
            exportProgress.value = null
        }
    }

    return {
        isExporting,
        exportProgress,
        exportAll,
    }
}
